/**
 * Snapshot bytes taken but not yet visible in the committed files list.
 *
 * Snapshots run in the background, so the budget effect can see a list from
 * before the last run's setFiles landed; these entries are counted on top so
 * the 80 MB cap is not undercounted in that window.
 *
 * An entry goes away when:
 * - its file shows up WITH its bytes (now counted from the list itself), or
 * - its run has finished (`settled`) and its file, once listed (`seen`), is
 *   gone: removed, or uploaded and cleared. Without this they stayed counted
 *   for the rest of the page and later picks got no snapshot budget at all.
 * A file never seen in the list is kept: its add may not have committed yet.
 * `everListed` (every file any committed list has shown) settles that for a
 * snapshot recorded after its file had already come and gone: such an entry
 * was never seen by this function, yet its file is gone for good.
 */
export interface AwaitingSnapshot {
  size: number;
  /** The run that took it has finished and applied its setFiles. */
  settled: boolean;
  /** The file has been seen in the committed list at least once. */
  seen: boolean;
}

/** Prunes `awaiting` against `files` and returns the bytes still awaiting. */
export function settleAwaitingSnapshots(
  awaiting: Map<File, AwaitingSnapshot>,
  files: ReadonlyArray<{ file: File; bytes?: unknown }>,
  everListed?: { has(file: File): boolean },
): number {
  const listed = new Map(files.map((f) => [f.file, f]));
  let total = 0;
  awaiting.forEach((entry, file) => {
    const item = listed.get(file);
    if (!entry.seen && everListed?.has(file)) entry.seen = true;
    if (item) {
      entry.seen = true;
      if (item.bytes) {
        awaiting.delete(file);
        return;
      }
    } else if (entry.seen && entry.settled) {
      awaiting.delete(file);
      return;
    }
    total += entry.size;
  });
  return total;
}
